import { useMemo } from "react";
import { Icon } from "@/components/Icon/Icon";
import { COMPOUND_LIB } from "@/services/mock";
import type { CompositionRow } from "@/services/composition_api";
import { HeatmapRow } from "./AutoFormulate/HeatmapRow";
import { ScanBeam } from "./AutoFormulate/ScanBeam";
import { vectorFor } from "./AutoFormulate/profileVector";

type CompositionAsideProps = {
  rows: CompositionRow[];
  foodName: string;
  sourceLabel?: string | null;
  inFlaskIds: Set<string>;
  onClose: () => void;
  // Optional — when present, an inline "+" button appears next to rows whose
  // compound name resolves to a library entry not already in the flask.
  onAdd?: (compoundId: string) => void;
  /** When true, swap composition rows for grayscale heatmap rows — matches
   *  the reveal pattern of the Current Flask body during AI formulating, so
   *  the user sees target-vs-current as two parallel vector stacks. */
  aiActive?: boolean;
};

// Reference food's molecular composition (GC-MS scan), shown beside the
// Current flask rows so the flavorist can compare what the *real food*
// contains against what they've put in the flask.
export function CompositionAside({
  rows: rawRows,
  foodName,
  sourceLabel = null,
  inFlaskIds,
  onClose,
  onAdd,
  aiActive = false,
}: CompositionAsideProps) {
  // Resolve composition.compound name → library id, lower-case keyed.
  const idByName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of COMPOUND_LIB) m.set(c.name.toLowerCase(), c.id);
    return m;
  }, []);
  const lookupId = (name: string): string | null =>
    idByName.get(name.toLowerCase()) ?? null;

  const rows = useMemo(
    () => [...rawRows].sort((a, b) => b.ratio - a.ratio),
    [rawRows]
  );
  const total = rows.reduce((s, r) => s + r.ratio, 0);
  const max = rows.reduce((m, r) => Math.max(m, r.ratio), 0) || 1;

  return (
    <div
      style={{
        width: 280,
        flexShrink: 0,
        borderRight: "1px solid var(--line)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        background: "var(--bg-sunk)",
      }}
    >
      <div
        className="hstack"
        style={{
          padding: "10px 12px 10px 14px",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          minHeight: 44,
          borderBottom: "1px solid var(--line-2)",
        }}
      >
        <div
          className="vstack"
          style={{ gap: 1, lineHeight: 1.15, minWidth: 0 }}
        >
          <span className="label" style={{ margin: 0, color: "var(--ink)" }}>
            Food composition
          </span>
          <span
            className="mono"
            style={{
              fontSize: 9,
              color: "var(--ink-4)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {foodName}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          title="Hide composition"
          className="btn xs ghost"
          style={{ padding: "3px 5px" }}
        >
          <Icon name="x" size={12} />
        </button>
      </div>

      <div
        className="hstack mono"
        style={{
          fontSize: 10,
          color: "var(--ink-4)",
          padding: "6px 12px 4px",
          gap: 6,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          borderBottom: "1px solid var(--line-2)",
        }}
      >
        {aiActive ? (
          <span style={{ flex: 1 }}>Vector profile</span>
        ) : (
          <>
            <span style={{ flex: 1 }}>Compound</span>
            <span style={{ width: 64, textAlign: "right" }}>% ratio</span>
          </>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", position: "relative" }}>
        {aiActive && <ScanBeam />}
        {aiActive
          ? rows.map((r, i) => {
              const cid = lookupId(r.compound);
              const lib = cid
                ? COMPOUND_LIB.find((c) => c.id === cid)
                : undefined;
              return (
                <HeatmapRow
                  key={i}
                  name={r.compound}
                  family={lib?.family ?? "neutral"}
                  kind="compound"
                  vector={vectorFor(`composition:${r.compound}`, lib?.family ?? "neutral")}
                  tone="gray"
                />
              );
            })
          : rows.map((r, i) => {
          const barW = (r.ratio / max) * 100;
          const cid = lookupId(r.compound);
          const inFlask = !!cid && inFlaskIds.has(cid);
          const canAdd = !!cid && !inFlask && !!onAdd;
          return (
            <div
              key={i}
              className="composition-row hstack"
              style={{
                padding: "7px 12px",
                gap: 6,
                borderBottom: "1px solid var(--line-2)",
                position: "relative",
              }}
            >
              <div
                className="vstack"
                style={{ flex: 1, minWidth: 0, lineHeight: 1.2, gap: 3 }}
              >
                <span
                  className="hstack"
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    gap: 5,
                    alignItems: "center",
                    color: inFlask ? "var(--ink-3)" : "var(--ink)",
                    minWidth: 0,
                  }}
                >
                  {inFlask && <Icon name="check" size={11} />}
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.compound}
                  </span>
                </span>
                <div
                  style={{
                    height: 3,
                    minHeight: 3,
                    maxHeight: 3,
                    flexShrink: 0,
                    background: "var(--line-2)",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${barW}%`,
                      height: 3,
                      background: "var(--ink-2)",
                      opacity: inFlask ? 0.3 : 0.55,
                    }}
                  />
                </div>
              </div>
              <span
                className="mono"
                style={{
                  width: 64,
                  textAlign: "right",
                  fontSize: 11.5,
                  color: inFlask ? "var(--ink-4)" : "var(--ink-2)",
                  fontWeight: 500,
                }}
              >
                {r.ratio.toFixed(1)}%
              </span>
              {canAdd && (
                <button
                  type="button"
                  onClick={() => onAdd(cid as string)}
                  className="composition-add btn xs ghost"
                  title={`Add ${r.compound} to current flask`}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    padding: "3px 5px",
                    background: "var(--bg)",
                    border: "1px solid var(--line)",
                  }}
                >
                  <Icon name="plus" size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div
        className="vstack"
        style={{
          padding: "8px 12px",
          borderTop: "1px solid var(--line)",
          gap: 3,
        }}
      >
        <div
          className="hstack"
          style={{ justifyContent: "space-between", fontSize: 10 }}
        >
          <span
            className="mono"
            style={{
              color: "var(--ink-4)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Detected
          </span>
          <span className="mono" style={{ color: "var(--ink-2)" }}>
            {rows.length} compounds · {total.toFixed(1)}%
          </span>
        </div>
        {sourceLabel && (
          <span
            className="mono"
            style={{
              fontSize: 9,
              color: "var(--ink-4)",
              lineHeight: 1.35,
            }}
          >
            {sourceLabel}
          </span>
        )}
      </div>
    </div>
  );
}
